/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging */

import { css, CSSResultGroup, html, LitElement, PropertyValues, unsafeCSS } from "lit";
import { property } from "lit/decorators.js";

import { addCustomState, AttachInternals, deleteCustomState, setCustomState } from "../mixins";
import { DesignToken } from "../tokens";
import { prefersReducedMotion } from "../utils";
import { customElement } from "../decorators";
import { CollapsibleOrientation } from "./CollapsibleOrientation";

/**
 * A container used to expand and collapse content.
 *
 * @example
 * ```html
 * <m3e-collapsible>
 *  <!-- Collapsible content -->
 * </m3e-collapsible>
 * ```
 *
 * @tag m3e-collapsible
 *
 * @slot - Renders the collapsible content.
 *
 * @attr open - Whether content is visible.
 * @attr orientation - Orientation of collapsible content.
 * @attr no-animate - Whether to disable animation.
 *
 * @fires opening - Dispatched when the collapsible begins to open.
 * @fires opened - Dispatched when the collapsible has opened.
 * @fires closing - Dispatched when the collapsible begins to close.
 * @fires closed - Dispatched when the collapsible has closed.
 *
 * @cssprop --m3e-collapsible-animation-duration - The duration of the expand / collapse animation.
 */
@customElement("m3e-collapsible")
export class M3eCollapsibleElement extends AttachInternals(LitElement) {
  /** The styles of the element. */
  static override styles: CSSResultGroup = css`
    :host {
      display: block;
      overflow: hidden;
    }
    :host([orientation="vertical"]) {
      height: 0px;
      transition: ${unsafeCSS(`visibility var(--m3e-collapsible-animation-duration, ${DesignToken.motion.duration.medium1})
          ${DesignToken.motion.easing.standard},
        height var(--m3e-collapsible-animation-duration, ${DesignToken.motion.duration.medium1})
          ${DesignToken.motion.easing.standard},
        padding-top var(--m3e-collapsible-animation-duration, ${DesignToken.motion.duration.medium1})
          ${DesignToken.motion.easing.standard},
        padding-bottom var(--m3e-collapsible-animation-duration, ${DesignToken.motion.duration.medium1})
          ${DesignToken.motion.easing.standard}`)};
    }
    :host([orientation="horizontal"]) {
      width: 0px;
      transition: ${unsafeCSS(`visibility var(--m3e-collapsible-animation-duration, ${DesignToken.motion.duration.medium1})
          ${DesignToken.motion.easing.standard},
        width var(--m3e-collapsible-animation-duration, ${DesignToken.motion.duration.medium1})
          ${DesignToken.motion.easing.standard},
        padding-left var(--m3e-collapsible-animation-duration, ${DesignToken.motion.duration.medium1})
          ${DesignToken.motion.easing.standard},
        padding-right var(--m3e-collapsible-animation-duration, ${DesignToken.motion.duration.medium1})
          ${DesignToken.motion.easing.standard}`)};
    }
    :host(:not(:is(:state(--closing), :--closing)):not([open])) {
      visibility: hidden;
    }
    :host([orientation="vertical"]:not([open])) {
      min-height: unset !important;
      padding-top: 0px !important;
      padding-bottom: 0px !important;
    }
    :host([orientation="horizontal"]:not([open])) {
      min-width: unset !important;
      padding-left: 0px !important;
      padding-right: 0px !important;
    }
    :host([no-animate]),
    :host(:is(:state(--no-animate), :--no-animate)) {
      transition-duration: 0ms;
    }
    :host([orientation="vertical"]:is(:state(--opening), :--opening)),
    :host([orientation="vertical"]:is(:state(--closing), :--closing)) {
      overflow-y: hidden !important;
    }
    :host([orientation="horizontal"]:is(:state(--opening), :--opening)),
    :host([orientation="horizontal"]:is(:state(--closing), :--closing)) {
      overflow-x: hidden !important;
    }
    :host(:is(:state(--overflows), :--overflows)) {
      scrollbar-gutter: stable;
    }
    ::slotted(*) {
      --m3e-collapsible-animation-duration: initial;
    }
    @media (prefers-reduced-motion) {
      :host {
        transition: none;
      }
    }
  `;

  /** @private */ #slotChanged = false;
  /** @private */ #hasOpened = false;

  /**
   * Whether content is visible.
   * @default false
   */
  @property({ type: Boolean, reflect: true }) open = false;

  /**
   * Orientation of collapsible content.
   * @default "vertical"
   */
  @property({ reflect: true }) orientation: CollapsibleOrientation = "vertical";

  /**
   * Whether to disable animation.
   * @default false
   */
  @property({ attribute: "no-animate", type: Boolean, reflect: true }) noAnimate = false;

  /** @inheritdoc */
  protected override update(changedProperties: PropertyValues): void {
    super.update(changedProperties);

    addCustomState(this, "--no-animate");

    if (!this.#slotChanged) {
      if (this.open) {
        this.#hasOpened = true;
        this.#autoSize();
      }
      this.#slotChanged = true;
      return;
    }

    this.toggleAttribute("inert", !this.open);

    if (this.open) {
      this.#hasOpened = true;

      if (!(prefersReducedMotion() || this.noAnimate)) {
        this.#autoSize();
        setCustomState(
          this,
          "--overflows",
          this.orientation === "vertical" ? this.clientHeight < this.scrollHeight : this.clientWidth < this.scrollWidth,
        );
        this.#clearSize();
      }

      deleteCustomState(this, "--closing");
      addCustomState(this, "--opening");
      this.dispatchEvent(new Event("opening"));

      this.#clearSize();
      deleteCustomState(this, "--no-animate");
      this.#actualSize();

      if (prefersReducedMotion() || this.noAnimate) {
        this.#autoSize();
        deleteCustomState(this, "--opening");
        this.dispatchEvent(new Event("opened"));
      } else {
        this.addEventListener(
          "transitionend",
          () => {
            if (this.open) {
              this.#autoSize();
              deleteCustomState(this, "--opening");
              this.dispatchEvent(new Event("opened"));
            }
          },
          { once: true },
        );
      }
    } else {
      deleteCustomState(this, "--opening");
      addCustomState(this, "--closing");
      this.dispatchEvent(new Event("closing"));

      this.#actualSize();
      if (this.#hasOpened) {
        deleteCustomState(this, "--no-animate");
      }

      if (prefersReducedMotion() || this.noAnimate) {
        this.#clearSize();
        deleteCustomState(this, "--closing");
        this.dispatchEvent(new Event("closed"));
      } else {
        requestAnimationFrame(() => {
          this.#clearSize();
          this.addEventListener(
            "transitionend",
            () => {
              if (!this.open) {
                deleteCustomState(this, "--closing");
                this.dispatchEvent(new Event("closed"));
              }
            },
            { once: true },
          );
        });
      }
    }
  }

  /** @inheritdoc */
  protected override render(): unknown {
    return html`<slot @slotchange="${this.#handleSlotChange}"></slot>`;
  }

  /** @private */
  #handleSlotChange() {
    this.#slotChanged = true;
  }

  /** @private */
  #autoSize(): void {
    this.style[this.orientation === "vertical" ? "height" : "width"] = "auto";
  }

  /** @private */
  #clearSize(): void {
    this.style[this.orientation === "vertical" ? "height" : "width"] = "";
  }

  /** @private */
  #actualSize(): void {
    if (this.orientation === "vertical") {
      this.style.height = `${this.scrollHeight}px`;
    } else {
      this.style.width = `${this.scrollWidth}px`;
    }
  }
}

interface M3eCollapsibleElementEventMap extends HTMLElementEventMap {
  opening: Event;
  opened: Event;
  closing: Event;
  closed: Event;
}

export interface M3eCollapsibleElement {
  addEventListener<K extends keyof M3eCollapsibleElementEventMap>(
    type: K,
    listener: (this: M3eCollapsibleElement, ev: M3eCollapsibleElementEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions,
  ): void;

  addEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void;

  removeEventListener<K extends keyof M3eCollapsibleElementEventMap>(
    type: K,
    listener: (this: M3eCollapsibleElement, ev: M3eCollapsibleElementEventMap[K]) => void,
    options?: boolean | EventListenerOptions,
  ): void;

  removeEventListener(
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ): void;
}

declare global {
  interface HTMLElementTagNameMap {
    "m3e-collapsible": M3eCollapsibleElement;
  }
}
